import { ChevronDown } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { CategoryItem } from "../../types";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

/**
 * Modal dialog for moving a section to a different category.
 *
 * @param open - Controls dialog visibility
 * @param setOpen - Callback to update dialog visibility
 * @param categories - Available categories to move the section to
 * @param onConfirm - Callback invoked with the selected category when Move is clicked
 */
interface MoveToAlertDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  categories: CategoryItem[];
  onConfirm: (category: CategoryItem) => void;
}

export const MoveToAlertDialog: FC<MoveToAlertDialogProps> = ({
  open,
  setOpen,
  categories,
  onConfirm,
}) => {
  const { formatMessage } = useIntl();
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState(false);

  // No auto-selection: User must explicitly choose a category. (important-comment)
  // Value is reset to null when dialog opens to ensure clean state. (important-comment)
  const [value, setValue] = useState<number | null>(null);

  // Reset selection when dialog opens (controlled dialog pattern) (important-comment)
  useEffect(() => {
    if (open) {
      setValue(null);
    }
  }, [open]);

  const handleConfirm = (value: number | null) => {
    if (value === null) return;
    const category = categories.find((c) => c.id === value);
    if (!category) return;
    onConfirm(category);
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage
              id="docList.moveToDialog.title"
              defaultMessage="Move To..."
            />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="docList.moveToDialog.description"
              defaultMessage="Select the Category to which the document(s) will be transferred"
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-2">
          <Label>
            <FormattedMessage
              id="docList.moveToDialog.newCategory"
              defaultMessage="Select new category"
            />
          </Label>
          <Popover
            open={openCategoryDropdown}
            onOpenChange={setOpenCategoryDropdown}
            modal={true}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openCategoryDropdown}
                className="w-full justify-between uppercase"
              >
                {value !== null
                  ? categories.find((c) => c.id === value)?.name
                  : formatMessage({
                      id: "docList.moveToDialog.selectCategoryPlaceholder",
                      defaultMessage: "Choose category...",
                    })}
                <ChevronDown className="opacity-50 w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="min-w-[465px] p-0 cursor-pointer">
              <Command
                filter={(_, search, keywords = []) => {
                  const extendValue = keywords.join(" ");
                  if (
                    extendValue.toLowerCase().includes(search.toLowerCase())
                  ) {
                    return 1;
                  }
                  return 0;
                }}
              >
                <CommandInput
                  placeholder={formatMessage({
                    id: "docList.moveToDialog.placeholder",
                    defaultMessage: "Start typing",
                  })}
                />
                <CommandList className="max-h-[200px]">
                  <CommandEmpty>
                    <FormattedMessage
                      id="docList.moveToDialog.noFound"
                      defaultMessage="No category found"
                    />
                  </CommandEmpty>
                  <CommandGroup>
                    {categories.map((category) => (
                      <CommandItem
                        key={category.id}
                        value={String(category.id)}
                        keywords={[category.name]}
                        onSelect={(currentValue) => {
                          const selectedCategory = categories.find(
                            (c) => String(c.id) === currentValue
                          );
                          if (selectedCategory) {
                            setValue(selectedCategory.id);
                            setOpenCategoryDropdown(false);
                          }
                        }}
                        className="uppercase"
                      >
                        {category.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setOpen(false)}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          {/* Move button is disabled until a category is selected (important-comment) */}
          <AlertDialogAction
            onClick={() => handleConfirm(value)}
            disabled={value === null}
          >
            <FormattedMessage
              id="docList.moveToDialog.confirm"
              defaultMessage="Move"
            />
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
